import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/utils/apiClient";
import { useAuth } from "@/state/auth";

export interface AiToolRecord {
    tool_id: string;
    tool_name: string;
    category: string | null;
    description: string | null;
    is_enabled: boolean;
    created_at: string | null;
    updated_at: string | null;
}

interface ToolsResponse {
    tools: AiToolRecord[];
}

interface ToolResponse {
    tool: AiToolRecord;
}

export const useAiTools = () => {
    const profile = useAuth((state) => state.profile);
    return useQuery({
        queryKey: ["admin", "ai-tools"],
        queryFn: async () => {
            const data = await apiRequest<ToolsResponse>("/api/admin/tools");
            return data.tools;
        },
        enabled: Boolean(profile),
        staleTime: 5 * 60 * 1000, // 5 minutes
    });
};

export const useUpdateAiTool = () => {
    const profile = useAuth((state) => state.profile);
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async ({ tool_id, is_enabled }: { tool_id: string; is_enabled: boolean }) => {
            const data = await apiRequest<ToolResponse>(
                `/api/admin/tools/${encodeURIComponent(tool_id)}`,
                {
                    method: "PUT",
                    body: JSON.stringify({ is_enabled }),
                }
            );
            return data.tool;
        },
        onSuccess: () => {
            if (profile) {
                queryClient.invalidateQueries({ queryKey: ["admin", "ai-tools"] });
            }
        },
    });
};

export const useBulkUpdateAiTools = () => {
    const profile = useAuth((state) => state.profile);
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: async (updates: { tool_id: string; is_enabled: boolean }[]) => {
            const data = await apiRequest<ToolsResponse>(
                "/api/admin/tools/bulk",
                {
                    method: "POST",
                    body: JSON.stringify({ updates }),
                }
            );
            return data.tools;
        },
        onSuccess: () => {
            if (profile) {
                queryClient.invalidateQueries({ queryKey: ["admin", "ai-tools"] });
            }
        },
    });
};
